import { useMemo } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';

/** Set `VITE_GOOGLE_MAPS_API_KEY` in `.env`. */
const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const libraries = ['geometry', 'drawing', 'places'];

const mapContainerStyle = {
  height: '400px',
  width: '100%',
  borderRadius: '20px',
};

const defaultCenter = { lat: 43.082857, lng: -89.3676184 };

function parseCoord(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function Map({ location, handleClick, readOnly = false }) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: API_KEY ?? '',
    libraries,
  });

  const center = useMemo(
    () => ({
      lat: parseCoord(location?.latitude, defaultCenter.lat),
      lng: parseCoord(location?.longitude, defaultCenter.lng),
    }),
    [location?.latitude, location?.longitude],
  );

  const onMapClick = (e) => {
    if (readOnly || !handleClick || !e.latLng) {
      return;
    }
    handleClick({
      ...(location ?? {}),
      latitude: e.latLng.lat(),
      longitude: e.latLng.lng(),
    });
  };

  if (loadError) {
    return (
      <div style={{ ...mapContainerStyle, padding: '1rem' }}>
        Map could not be loaded. Check your Google Maps API key and network
        connection.
      </div>
    );
  }

  if (!isLoaded) {
    return <div style={mapContainerStyle} />;
  }

  if (!API_KEY?.trim()) {
    return (
      <div style={{ ...mapContainerStyle, padding: '1rem' }}>
        Set <code>VITE_GOOGLE_MAPS_API_KEY</code> in your environment to enable
        the map.
      </div>
    );
  }

  return (
    <GoogleMap
      key={`${center.lat}-${center.lng}`}
      mapContainerStyle={mapContainerStyle}
      zoom={17}
      center={center}
      onClick={onMapClick}
    >
      <Marker position={center} />
    </GoogleMap>
  );
}
